import React, { useState } from 'react';
import { X, Users, User } from 'lucide-react';
import { Button } from '../ui/button';

const GeneratePayoutEnhancedModal = ({ isOpen, onClose, onGenerate, tutors }) => {
  const [generationType, setGenerationType] = useState('all'); // 'all' or 'single'
  const [formData, setFormData] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    tutor_id: '',
    description: ''
  });
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0, show: false });

  if (!isOpen) return null;

  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];

  const years = [];
  const currentYear = new Date().getFullYear();
  for (let i = currentYear - 2; i <= currentYear + 1; i++) {
    years.push(i);
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsGenerating(true);
    
    const payoutData = {
      month: parseInt(formData.month),
      year: parseInt(formData.year),
      tutor_ids: generationType === 'single' ? [formData.tutor_id] : null,
      description: formData.description || undefined
    };

    // Show progress bar
    setProgress({ current: 0, total: 1, show: true });
    
    await onGenerate(payoutData, (current, total) => {
      setProgress({ current, total, show: true });
    });
    
    setIsGenerating(false);
    setProgress({ current: 0, total: 0, show: false });
    
    // Reset form
    setFormData({
      month: new Date().getMonth() + 1,
      year: new Date().getFullYear(),
      tutor_id: '',
      description: ''
    });
    setGenerationType('all');
  };

  const handleClose = () => {
    if (!isGenerating) {
      onClose();
      setProgress({ current: 0, total: 0, show: false });
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-lg w-full p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Generate Payouts</h2>
          <button
            onClick={handleClose}
            disabled={isGenerating}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Generation Type Selection */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
              Generation Type
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setGenerationType('all')}
                className={`p-4 rounded-lg border-2 transition-all flex flex-col items-center gap-2 ${
                  generationType === 'all'
                    ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                    : 'border-gray-300 dark:border-gray-600 hover:border-green-300'
                }`}
              >
                <Users className={`w-6 h-6 ${generationType === 'all' ? 'text-green-600' : 'text-gray-500'}`} />
                <span className={`font-semibold ${generationType === 'all' ? 'text-green-700 dark:text-green-400' : 'text-gray-700 dark:text-gray-300'}`}>
                  All Tutors
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400">Generate for all registered tutors</span>
              </button>
              <button
                type="button"
                onClick={() => setGenerationType('single')}
                className={`p-4 rounded-lg border-2 transition-all flex flex-col items-center gap-2 ${
                  generationType === 'single'
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-300 dark:border-gray-600 hover:border-blue-300'
                }`}
              >
                <User className={`w-6 h-6 ${generationType === 'single' ? 'text-blue-600' : 'text-gray-500'}`} />
                <span className={`font-semibold ${generationType === 'single' ? 'text-blue-700 dark:text-blue-400' : 'text-gray-700 dark:text-gray-300'}`}>
                  Single Tutor
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400">Generate for one tutor</span>
              </button>
            </div>
          </div>

          {/* Month and Year Selection */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Month
              </label>
              <select
                value={formData.month}
                onChange={(e) => setFormData({...formData, month: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                required
              >
                {months.map(month => (
                  <option key={month.value} value={month.value}>{month.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Year
              </label>
              <select
                value={formData.year}
                onChange={(e) => setFormData({...formData, year: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                required
              >
                {years.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Tutor Selection (only for single) */}
          {generationType === 'single' && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Select Tutor
              </label>
              <select
                value={formData.tutor_id}
                onChange={(e) => setFormData({...formData, tutor_id: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                required
              >
                <option value="">Choose a tutor...</option>
                {tutors.map(tutor => (
                  <option key={tutor.id} value={tutor.id}>{tutor.full_name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Description */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Description (Optional)
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              rows="2"
              placeholder={`e.g., Payout for ${months.find(m => m.value === parseInt(formData.month))?.label} ${formData.year}`}
            />
          </div>

          {/* Progress Bar */}
          {progress.show && (
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-blue-700 dark:text-blue-400">
                  Processing Payouts...
                </span>
                <span className="text-sm text-blue-600 dark:text-blue-400">
                  {progress.current} / {progress.total}
                </span>
              </div>
              <div className="w-full bg-blue-200 dark:bg-blue-800 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progress.total > 0 ? (progress.current / progress.total) * 100 : 0}%` }}
                />
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              onClick={handleClose}
              variant="outline"
              className="flex-1"
              disabled={isGenerating}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
              disabled={isGenerating}
            >
              {isGenerating ? 'Generating...' : 'Generate Payouts'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GeneratePayoutEnhancedModal;
